import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useUpdate, useNotify, useGetOne } from 'react-admin';
import { InsuranceFormFields } from './InsuranceFormFields';

interface InsuranceEditDialogProps {
  open: boolean;
  onClose: () => void;
  insuranceId: string | null;
}

export const InsuranceEditDialog = ({ open, onClose, insuranceId }: InsuranceEditDialogProps) => {
  const [update, { isLoading: isUpdating }] = useUpdate();
  const { data: insurance, isLoading: isLoadingData } = useGetOne(
    'insurance',
    { id: insuranceId || '' },
    { enabled: !!insuranceId && open }
  );
  const notify = useNotify();

  const [formData, setFormData] = React.useState({
    policyNumber: '',
    insurerName: '',
    policyName: '',
    effectiveDate: '',
    expiryDate: '',
    active: true,
    liabilityLimit: '',
    collisionDeductible: '',
    comprehensiveDeductible: '',
    allPerilsDeductible: '',
    notes: '',
  });

  useEffect(() => {
    if (insurance) {
      setFormData({
        policyNumber: insurance.policyNumber || '',
        insurerName: insurance.insurerName || '',
        policyName: insurance.policyName || '',
        effectiveDate: insurance.effectiveDate || '',
        expiryDate: insurance.expiryDate || '',
        active: insurance.active !== undefined ? insurance.active : true,
        liabilityLimit: insurance.liabilityLimit?.toString() || '',
        collisionDeductible: insurance.collisionDeductible?.toString() || '',
        comprehensiveDeductible: insurance.comprehensiveDeductible?.toString() || '',
        allPerilsDeductible: insurance.allPerilsDeductible?.toString() || '',
        notes: insurance.notes || '',
      });
    }
  }, [insurance]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!insuranceId) return;

    if (!formData.policyNumber || !formData.insurerName || !formData.effectiveDate || !formData.expiryDate) {
      notify('Please fill in all required fields', { type: 'warning' });
      return;
    }

    const dataToSubmit: any = {
      policyNumber: formData.policyNumber,
      insurerName: formData.insurerName,
      effectiveDate: formData.effectiveDate,
      expiryDate: formData.expiryDate,
      active: formData.active,
    };

    if (formData.policyName) dataToSubmit.policyName = formData.policyName;
    if (formData.liabilityLimit) dataToSubmit.liabilityLimit = Number(formData.liabilityLimit);
    if (formData.collisionDeductible) dataToSubmit.collisionDeductible = Number(formData.collisionDeductible);
    if (formData.comprehensiveDeductible) dataToSubmit.comprehensiveDeductible = Number(formData.comprehensiveDeductible);
    if (formData.allPerilsDeductible) dataToSubmit.allPerilsDeductible = Number(formData.allPerilsDeductible);
    if (formData.notes) dataToSubmit.notes = formData.notes;

    update(
      'insurance',
      { id: insuranceId, data: dataToSubmit },
      {
        onSuccess: () => {
          notify('Insurance policy updated successfully', { type: 'success' });
          onClose();
        },
        onError: (error: any) => {
          notify(`Error updating insurance policy: ${error.message}`, { type: 'error' });
        },
      }
    );
  };

  const handleClose = () => {
    if (!isUpdating) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Insurance Policy</DialogTitle>
      <DialogContent>
        {isLoadingData ? (
          <div>Loading...</div>
        ) : (
          <InsuranceFormFields formData={formData} handleChange={handleChange} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isUpdating}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isUpdating || isLoadingData}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
